import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Check, X, Plus, FileText, Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, Trash2, Hand, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getPhotoUrl } from '@/utils/photoUrl';
import { useAuth } from '@/contexts/AuthContext';
import NewManualOfferDialog from '@/components/ofertas/NewManualOfferDialog';
import { SortableTh } from '@/components/ui/SortableTh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
};

const Ofertas = () => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
  const isSuperAdmin = user?.rol === 'SuperAdmin' || user?.isSuperAdmin;
  const [ofertas, setOfertas] = useState([]);
  const [vehiculos, setVehiculos] = useState({});
  const [revendedores, setRevendedores] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [processingOffer, setProcessingOffer] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentTab, setCurrentTab] = useState('nuevas');
  const [sortField, setSortField] = useState('hora');
  const [sortDirection, setSortDirection] = useState('desc');
  const pageSize = 10;
  const serverChunkSize = 100;

  useEffect(() => {
    loadDictionaries();
  }, []);

  // Debounce search input
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
    loadOfertasChunk(0);
  }, [searchTerm, currentTab, sortField, sortDirection]);

  useEffect(() => {
    const requiredStartIndex = (currentPage - 1) * pageSize;
    if (requiredStartIndex >= ofertas.length && ofertas.length < totalCount) {
      loadOfertasChunk(ofertas.length);
    }
  }, [currentPage]);

  const loadDictionaries = async () => {
    try {
      const [vehiculosRes, revendedoresRes] = await Promise.all([
        axios.get(`${API}/vehiculos?includeDeleted=true`),
        axios.get(`${API}/revendedores?includeDeleted=true`),
      ]);
      const vehMap = {};
      vehiculosRes.data.forEach((v) => { vehMap[v.id] = v; });
      setVehiculos(vehMap);
      const revMap = {};
      revendedoresRes.data.forEach((r) => { revMap[r.id] = r; });
      setRevendedores(revMap);
    } catch (error) {
      console.error('Error loading dictionaries:', error);
    }
  };

  const loadOfertasChunk = async (skip) => {
    try {
      setLoading(true);
      const estadoFilter = currentTab === 'nuevas' 
        ? 'Pendiente' 
        : currentTab === 'aceptadas' 
        ? 'Aceptada' 
        : currentTab === 'rechazadas' 
        ? 'Rechazada' 
        : 'papelera';

      const res = await axios.get(`${API}/ofertas/paginated`, {
        params: {
          skip,
          limit: serverChunkSize,
          searchTerm: searchTerm || undefined,
          estadoFilter,
          sortField,
          sortDirection
        }
      });
      const { items, total, vehiculos: resVehiculos, revendedores: resRevendedores } = res.data;
      setTotalCount(total);

      // Sincronizar vehículos y revendedores devueltos por el backend para asegurar datos completos
      if (resVehiculos && resVehiculos.length > 0) {
        setVehiculos(prev => {
          const updated = { ...prev };
          resVehiculos.forEach(v => { updated[v.id] = v; });
          return updated;
        });
      }
      if (resRevendedores && resRevendedores.length > 0) {
        setRevendedores(prev => {
          const updated = { ...prev };
          resRevendedores.forEach(r => { updated[r.id] = r; });
          return updated;
        });
      }

      if (skip === 0) {
        setOfertas(items);
      } else {
        setOfertas(prev => {
          const newIds = items.map(i => i.id);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id));
          return [...filteredPrev, ...items];
        });
      }
    } catch (error) {
      console.error('Error loading ofertas chunk:', error);
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  const refreshFullState = () => {
    loadDictionaries();
    loadOfertasChunk(0);
  };

  const handleAccept = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      refreshFullState();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleReject = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      refreshFullState();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleSoftDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea mover esta oferta a la papelera?")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Oferta movida a la papelera');
      refreshFullState();
    } catch (error) {
      console.error('Error moving offer to trash:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta');
    }
  };

  const handleRestoreOffer = async (ofertaId) => {
    try {
      await axios.patch(`${API}/ofertas/${ofertaId}/restore`);
      toast.success('Oferta restaurada');
      refreshFullState();
    } catch (error) {
      console.error('Error restoring offer:', error);
      toast.error(error.response?.data?.detail || 'Error al restaurar oferta');
    }
  };

  const handlePermanentDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea eliminar permanentemente esta oferta? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}/permanent`);
      toast.success('Oferta eliminada permanentemente');
      refreshFullState();
    } catch (error) {
      console.error('Error permanently deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta definitivamente');
    }
  };


  const sortConfig = sortField ? { key: sortField, direction: sortDirection } : null;

  const requestSort = (field) => {
    if (sortConfig && sortConfig.key === field && sortConfig.direction === 'desc') {
      setSortDirection('asc');
    } else if (sortConfig && sortConfig.key === field && sortConfig.direction === 'asc') {
      setSortField('hora');
      setSortDirection('desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const currentDisplayOfertas = ofertas.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Ofertas</h1>
              <p className="text-muted-foreground mt-1">
                {totalCount} ofertas encontradas
              </p>
            </div>
            <Button onClick={() => setShowNewOfferDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Cargar Oferta Manual
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Búsqueda */}
        <Card className="p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por vehículo, revendedor o empresa..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchTerm(searchInput);
                }
              }}
              className="pl-10"
              data-testid="search-offer-input"
            />
          </div>
        </Card>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="nuevas" data-testid="offers-tab-new">
              Nuevas {currentTab === 'nuevas' ? `(${totalCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="aceptadas">
              Aceptadas {currentTab === 'aceptadas' ? `(${totalCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="rechazadas">
              Rechazadas {currentTab === 'rechazadas' ? `(${totalCount})` : ''}
            </TabsTrigger>
            <TabsTrigger 
              value="papelera" 
              data-testid="offers-tab-trash" 
              className="text-destructive/80 data-[state=active]:text-destructive gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Papelera {currentTab === 'papelera' ? `(${totalCount})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="space-y-4">
            {loading && ofertas.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">Cargando ofertas...</p>
              </Card>
            ) : totalCount === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No hay ofertas</p>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left p-4 font-medium w-20">Foto</th>
                        <SortableTh label="Vehículo" sortKey="vehiculo" sortConfig={sortConfig} requestSort={requestSort} />
                        <SortableTh label="Revendedor" sortKey="revendedor" sortConfig={sortConfig} requestSort={requestSort} />
                        <SortableTh label="Monto Ofrecido" sortKey="monto" sortConfig={sortConfig} requestSort={requestSort} />
                        <SortableTh label="Fecha" sortKey="hora" sortConfig={sortConfig} requestSort={requestSort} />
                        <th className="text-right p-4 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentDisplayOfertas.map((oferta) => {
                        const vehiculo = vehiculos[oferta.vehiculoId];
                        const revendedor = revendedores[oferta.revendedorId];
                        const isOfertaDeleted = !!oferta.deletedAt;
                        const isVehiculoDeleted = !!vehiculo?.deletedAt;

                        return (
                          <tr
                            key={oferta.id}
                            className={`border-b hover:bg-accent/50 transition-colors duration-200 ${
                              isOfertaDeleted ? 'bg-destructive/[0.03]' : ''
                            }`}
                            data-testid="offers-row"
                          >
                            <td className="p-4">
                              <div className={`w-16 h-16 rounded-md overflow-hidden bg-muted relative ${
                                isVehiculoDeleted ? 'opacity-80' : ''
                              }`}>
                                {vehiculo?.fotos && vehiculo.fotos[0] ? (
                                  <img 
                                    src={getPhotoUrl(vehiculo.fotos[0])} 
                                    alt={vehiculo?.marca || 'Vehículo'} 
                                    className="w-full h-full object-cover" 
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <FileText className="h-6 w-6 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="font-medium flex flex-wrap items-center gap-1.5">
                                {vehiculo ? (
                                  <Link to={`/publicaciones/${vehiculo.id}`} className="text-primary hover:underline cursor-pointer">
                                    {vehiculo.marca} {vehiculo.modelo}
                                  </Link>
                                ) : (
                                  <span className="text-muted-foreground italic">Vehículo no encontrado</span>
                                )}
                                {isVehiculoDeleted && (
                                  <Badge variant="destructive" className="text-[10px] h-4 px-1.5 font-normal">
                                    Vehículo en papelera
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {vehiculo ? `${vehiculo.version || ''} • ${vehiculo.anio || ''}` : ''}
                              </div>
                              {vehiculo?.precioBase && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Base: ${vehiculo.precioBase.toLocaleString('en-US')}
                                </div>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="font-medium flex flex-wrap items-center gap-1.5">
                                <span>{revendedor?.nombre || 'N/A'}</span>
                                {revendedor?.deletedAt && (
                                  <span className="text-xs text-muted-foreground" title={`Eliminado el ${new Date(revendedor.deletedAt).toLocaleDateString('es-UY')}`}>
                                    (eliminado)
                                  </span>
                                )}
                              </div>
                              {revendedor?.empresa && (
                                <div className="text-sm text-muted-foreground">{revendedor.empresa}</div>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-lg font-bold text-primary">
                                  ${oferta.monto.toLocaleString('en-US')}
                                </span>
                                {isOfertaDeleted && (
                                  <Badge variant="destructive" className="gap-1 text-[11px] h-5">
                                    <Trash2 className="h-3 w-3" />
                                    Oferta eliminada
                                  </Badge>
                                )}
                                {oferta.esManual && (
                                  <Badge variant="outline" className="gap-1 text-[11px] h-5 border-primary text-primary">
                                    <Hand className="h-3 w-3" />
                                    Manual
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              <div className="mb-1">{format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}</div>
                              {isOfertaDeleted ? (
                                <div className="text-destructive font-medium text-xs flex items-center gap-1">
                                  <Trash2 className="h-3 w-3" />
                                  Eliminada: {format(new Date(oferta.deletedAt), 'dd/MM/yyyy HH:mm')}
                                </div>
                              ) : oferta.estado === 'Aceptada' && oferta.fechaAceptacion ? (
                                <div className="text-green-600 dark:text-green-400 font-medium">
                                  Aceptada: {format(new Date(oferta.fechaAceptacion), 'dd/MM/yyyy')}
                                </div>
                              ) : null}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end gap-2 items-center">
                                {vehiculo && (
                                  <Button size="icon" variant="ghost" asChild title="Ver vehículo" className="h-8 w-8">
                                    <Link to={`/publicaciones/${vehiculo.id}`}>
                                      <Eye className="h-4 w-4" />
                                    </Link>
                                  </Button>
                                )}

                                {isOfertaDeleted ? (
                                  <>
                                    {isAdmin && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 gap-1.5 text-xs hover:bg-accent"
                                        onClick={() => handleRestoreOffer(oferta.id)}
                                        title="Restaurar oferta"
                                      >
                                        <RotateCcw className="h-3.5 w-3.5 text-primary" />
                                        Restaurar
                                      </Button>
                                    )}
                                    {isSuperAdmin && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => handlePermanentDeleteOffer(oferta.id)}
                                        title="Eliminar oferta definitivamente (SuperAdmin)"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {isAdmin && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => handleSoftDeleteOffer(oferta.id)}
                                        title="Mover oferta a papelera"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {oferta.estado === 'Pendiente' && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="default"
                                          disabled={!!processingOffer}
                                          onClick={() => handleAccept(oferta.id)}
                                          title="Aceptar oferta"
                                        >
                                          {processingOffer === oferta.id ? (
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                          ) : (
                                            <Check className="h-4 w-4" />
                                          )}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={!!processingOffer}
                                          onClick={() => handleReject(oferta.id)}
                                          title="Rechazar oferta"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / pageSize)}
              onPageChange={setCurrentPage}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* New Offer Dialog */}
      <NewManualOfferDialog
        open={showNewOfferDialog}
        onOpenChange={setShowNewOfferDialog}
        onSuccess={refreshFullState}
        vehiculos={vehiculos}
        revendedores={revendedores}
      />
    </div>
  );
};

export default Ofertas;